import { PhotoThumbnail } from "@/components/common/PhotoGrid"
import { useSpeciesProfile } from "@/hooks/useSpeciesProfile"

import { getAllImagesForSpecies, SpeciesProfile } from "@/lib/speciesProfilesDb"
import { createFileRoute } from "@tanstack/react-router"
import { AlertTriangle, ArrowLeft, Database, Eye } from "lucide-react"
import { Suspense, useCallback, useState } from "react"

import { PhotoViewer } from "@/components/common/PhotoViewer"
import { useDownloadAlaImages } from "@/hooks/useDownloadAlaImages"
import { useReferenceDataForSpecies } from "@/hooks/useReferenceData"
import { TaxonName } from "@fieldbook/common/components"
import { Button } from "@fieldbook/ui/button"
import { useSuspenseQuery } from "@tanstack/react-query"
import { ButtonLink } from "@fieldbook/ui/button-link"
import { useSpecies } from "@/hooks/useSpecies"
import type { Species } from "@fieldbook/common/types"
import { useALAImages, getSpeciesImageUrl } from "@fieldbook/common/hooks"
import { Spinner } from "@fieldbook/ui/spinner"
import { formatConservationCode } from "@fieldbook/common/utils"
import { CachedImage } from "@/components/common/CachedImage"
import { SpeciesDetailTabs } from "./-components/SpeciesDetailTabs"
import { speciesListSearchSchema } from "./-utils/speciesListTabs"

export const Route = createFileRoute(
  "/_private/projects/$id/data/species/$speciesId",
)({
  validateSearch: (search) => speciesListSearchSchema.parse(search),
  component: () => (
    <Suspense
      fallback={
        <div className="bg-muted flex w-full animate-pulse items-center rounded p-4" />
      }
    >
      <SpeciesDetailRoute />
    </Suspense>
  ),
})

function SpeciesDetailRoute() {
  const { id: projectId } = Route.useParams()
  const { tab } = Route.useSearch()
  const navigate = Route.useNavigate()

  return (
    <div className="flex flex-col">
      <button
        onClick={() =>
          navigate({
            to: "/projects/$id/data/species",
            params: { id: projectId },
            search: { tab },
          })
        }
        className="text-muted-foreground mb-4 mt-2 flex items-center gap-1 text-sm focus-visible:outline-0"
      >
        <ArrowLeft size={12} />
        <span>Species</span>
      </button>

      <SpeciesDetail />
    </div>
  )
}

function SpeciesDetail() {
  const { speciesId, id: projectId } = Route.useParams()
  const { data: speciesProfile } = useSpeciesProfile(speciesId)
  const { data: fieldbookSpecies } = useSpecies(speciesId)
  const species = fieldbookSpecies ?? speciesProfile

  const name = species?.name
  const commonName = speciesProfile?.commonName
  const family = fieldbookSpecies?.family ?? speciesProfile?.family
  const consCode =
    fieldbookSpecies?.metadata?.stateConservation?.WA?.status ??
    speciesProfile?.stateConservation
  const conservationStatus = formatConservationCode(consCode)

  // Check if species has reference data occurrences
  const { count: referenceDataCount, hasData: hasReferenceData } =
    useReferenceDataForSpecies(projectId, name)

  return (
    <>
      <div className="mb-2 flex flex-col gap-1">
        <div className="flex flex-col gap-1">
          <div className="flex gap-2">
            {name && <TaxonName name={name} className="text-xl" />}
          </div>
          {commonName && (
            <span className="text-muted-foreground text-sm">{commonName}</span>
          )}
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground text-sm uppercase">
              {family}
            </span>
          </div>
        </div>
        {consCode && (
          <span className="flex items-center gap-2 text-sm text-orange-500">
            <AlertTriangle size="14" />
            <span>{conservationStatus}</span>
          </span>
        )}
        <div className="flex flex-wrap justify-end gap-2">
          {hasReferenceData && name && (
            <ButtonLink
              size="sm"
              variant="outline"
              className="flex items-center gap-1"
              to="/projects/$id/data/context/species/$taxon"
              params={{ id: projectId, taxon: name }}
            >
              <Database size={16} />
              View {referenceDataCount} Reference Location
              {referenceDataCount !== 1 ? "s" : ""}
            </ButtonLink>
          )}
          <ButtonLink
            size="sm"
            className="flex items-center gap-1"
            to={`/projects/$id/observations/new/species`}
            search={{ speciesId: speciesId }}
            params={{ id: projectId }}
          >
            <Eye />
            Record Observation
          </ButtonLink>
        </div>
      </div>
      <SpeciesDetailTabs
        projectId={projectId}
        speciesId={speciesId}
        speciesName={name}
        photoContent={species ? <PhotoGrid species={species} /> : null}
      />
    </>
  )
}

const useSuspenseSpeciesImages = (speciesId: string) => {
  return useSuspenseQuery({
    queryKey: ["speciesProfileImages", "bySpecies", speciesId],
    queryFn: async () => {
      const images = await getAllImagesForSpecies(speciesId)
      return images ?? null
    },
    staleTime: 1000 * 60 * 60, // 1 hour
    meta: {
      persisted: false,
    },
  })
}

export function PhotoGrid({ species }: { species: SpeciesProfile | Species }) {
  const { data: images, refetch } = useSuspenseSpeciesImages(species.id)

  const [viewerOpen, setViewerOpen] = useState(false)
  const [initialIndex, setInitialIndex] = useState(0)

  const handlePhotoClick = useCallback((index: number) => {
    setInitialIndex(index)
    setViewerOpen(true)
  }, [])

  const { data: alaImages, imageIds: alaImageIds } = useALAImages(
    species.ala_guid,
    "thumbnail",
  )

  const alaImageCount = alaImages.length
  const profileImageCount =
    "imageCount" in species ? species.imageCount : alaImageCount

  const hasMoreAlaPhotos =
    images?.filter((i) => i.source === "ala").length < profileImageCount

  const missingImageIds = hasMoreAlaPhotos
    ? (alaImageIds?.filter((id) => !images?.find((i) => i.id === id)) ?? [])
    : []

  const { downloadAlaImages, isDownloading } = useDownloadAlaImages()
  const handleDownloadAll = useCallback(async () => {
    if (!species.ala_guid || isDownloading) return
    await downloadAlaImages({
      alaGuid: species.ala_guid,
      speciesProfileId: species.id,
    })
    refetch()
  }, [downloadAlaImages, species.ala_guid, species.id, isDownloading, refetch])

  const [downloadingImageId, setDownloadingImageId] = useState<string | null>(
    null,
  )

  const handleMissingImageClick = useCallback(
    async (imageId: string) => {
      if (!species.ala_guid || isDownloading || downloadingImageId) return
      setDownloadingImageId(imageId)

      await downloadAlaImages({
        alaGuid: species.ala_guid,
        imageIds: [imageId],
        speciesProfileId: species.id,
      })
      const { data: newImages } = await refetch()
      setDownloadingImageId(null)
      const photoIndex = newImages?.findIndex((p) => p.id === imageId)

      if (photoIndex !== undefined) {
        handlePhotoClick(photoIndex)
      }
    },
    [
      species.ala_guid,
      species.id,
      isDownloading,
      downloadAlaImages,
      downloadingImageId,
      refetch,
      handlePhotoClick,
    ],
  )

  return (
    <>
      {images && images.length > 0 && (
        <>
          <span className="text-muted-foreground">On Device</span>
          <div className="grid grid-cols-3 gap-2">
            {images.map(({ thumbnail, id }, index) => (
              <PhotoThumbnail
                key={id}
                blob={thumbnail}
                id={id}
                onClick={() => handlePhotoClick(index)}
              />
            ))}
          </div>
        </>
      )}
      {missingImageIds.length > 0 && (
        <>
          <span className="text-muted-foreground">More Photos</span>
          <div className="grid grid-cols-3 gap-2">
            {missingImageIds.map((id) => {
              const url = getSpeciesImageUrl({ id, format: "thumbnail" })
              if (!url) return null
              if (downloadingImageId === id) {
                return (
                  <div
                    key={id}
                    className="flex aspect-square w-full items-center justify-center rounded bg-opacity-50"
                  >
                    <Spinner size="large" />
                  </div>
                )
              }
              return (
                <button
                  key={id}
                  onClick={() => handleMissingImageClick(id)}
                  className="focus:outline-none"
                >
                  <CachedImage
                    src={url}
                    alt="species photo"
                    className="aspect-square w-full rounded object-cover"
                  />
                </button>
              )
            })}
          </div>
        </>
      )}
      {viewerOpen && images.length > 0 && (
        <PhotoViewer
          photos={images.map((img) => ({ id: img.id, blob: img.full }))}
          initialIndex={initialIndex}
          onClose={() => setViewerOpen(false)}
        />
      )}
      {hasMoreAlaPhotos && (
        <div className="mt-4 flex items-center justify-end gap-2">
          <Button onClick={handleDownloadAll} disabled={isDownloading}>
            {!isDownloading && `Download all ${profileImageCount} photos`}
            {isDownloading && "Downloading..."}
          </Button>
        </div>
      )}
    </>
  )
}
